
import React, { createContext, useContext, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { oidcConfig } from '../config/oidcConfig';
import { refreshTokens, clearRefreshToken } from '../services/tokenService';
import { configureApiClient } from '../services/apiService';
import { UserManager, User } from 'oidc-client-ts';

// Export a single UserManager instance so callback handling reuses same storage/state
export const userManager = new UserManager(oidcConfig);

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  isLoading: boolean;
  refreshUserTokens: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasStoredRefreshToken = useRef(false);
  const refreshInProgress = useRef(false);

  // Load user on mount
  useEffect(() => {
    setIsLoading(true);
    userManager.getUser().then((loadedUser) => {
      setUser(loadedUser);
      setIsAuthenticated(!!loadedUser && !loadedUser.expired);
      setIsLoading(false);
    });
    userManager.events.addUserLoaded((loadedUser) => {
      setUser(loadedUser);
      setIsAuthenticated(!!loadedUser && !loadedUser.expired);
    });
    userManager.events.addUserUnloaded(() => {
      setUser(null);
      setIsAuthenticated(false);
    });
    return () => {
      userManager.events.removeUserLoaded(() => {});
      userManager.events.removeUserUnloaded(() => {});
    };
  }, []);

  // Login
  const login = useCallback(() => {
    userManager.signinRedirect();
  }, []);

  // Logout
  const logout = useCallback(() => {
    hasStoredRefreshToken.current = false;
    clearRefreshToken().finally(() => {
      // Clear storage after capturing tokens
      localStorage.clear();
      sessionStorage.clear();
      // Use OIDC config value for post_logout_redirect_uri
    });
  }, []);

  // Function to refresh user tokens
  const refreshUserTokens = useCallback(async (): Promise<boolean> => {
    if (refreshInProgress.current) {
      return false;
    }
    try {
      refreshInProgress.current = true;
      if (!user?.refresh_token) {
        return false;
      }
      const newTokens = await refreshTokens(user.refresh_token);
      if (newTokens) {
        const updatedUser = {
          ...user,
          access_token: newTokens.access_token,
          id_token: newTokens.id_token,
          refresh_token: newTokens.refreshtoken
        };
        setUser(updatedUser as User);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      refreshInProgress.current = false;
    }
  }, [user]);

  // Configure API client when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      configureApiClient(() => user.id_token, refreshUserTokens);
    }
  }, [isAuthenticated, user, refreshUserTokens]);

  const contextValue: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    refreshUserTokens,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
// Custom hook to use the AuthContext
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
